import React, { useRef, useState } from "react";
import { Play, AlertCircle, Loader } from "lucide-react";

/**
 * VideoPlayer — lecteur vidéo léger pour les vidéos uploadées dans les annonces.
 * Supporte base64 et URLs directes. Chargement asynchrone pour économiser la bande passante.
 */
const VideoPlayer = ({ src, poster, label }) => {
  const videoRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | loading | playing | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleLoad = () => setStatus("playing");
  const handleError = () => {
    setStatus("error");
    setErrorMsg("Impossible de lire cette vidéo. Vérifiez votre connexion.");
  };

  const startPlay = () => {
    setStatus("loading");
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => setStatus("playing")); // autoplay blocked gracefully
    }
  };

  if (!src) return null;

  return (
    <div className="relative bg-neutral-900 rounded-xl overflow-hidden" data-testid="video-player">
      {/* Placeholder / play button */}
      {status === "idle" && (
        <div className="aspect-video flex flex-col items-center justify-center cursor-pointer group" onClick={startPlay}>
          {poster ? (
            <img src={poster} alt={label || "Vidéo"} className="absolute inset-0 w-full h-full object-cover opacity-60" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-900" />
          )}
          <div className="relative z-10 flex flex-col items-center gap-3">
            <div className="bg-[#FF6B1A] rounded-full p-5 shadow-xl group-hover:scale-110 transition-transform">
              <Play className="h-8 w-8 text-white fill-white" />
            </div>
            <span className="text-white/80 text-sm font-semibold">{label || "Lancer la vidéo"}</span>
          </div>
        </div>
      )}

      {/* Loading */}
      {status === "loading" && (
        <div className="aspect-video flex items-center justify-center bg-neutral-900">
          <div className="flex flex-col items-center gap-2">
            <Loader className="h-8 w-8 text-[#FF6B1A] animate-spin" />
            <span className="text-white/60 text-sm">Chargement…</span>
          </div>
          <video
            ref={videoRef}
            className="hidden"
            preload="auto"
            onCanPlay={handleLoad}
            onError={handleError}
          >
            <source src={src} />
          </video>
        </div>
      )}

      {/* Playing */}
      {status === "playing" && (
        <video
          ref={videoRef}
          className="w-full aspect-video"
          controls
          preload="metadata"
          onError={handleError}
          playsInline
        >
          <source src={src} />
          Votre navigateur ne supporte pas la lecture vidéo.
        </video>
      )}

      {/* Error */}
      {status === "error" && (
        <div className="aspect-video flex flex-col items-center justify-center gap-3 bg-neutral-900">
          <AlertCircle className="h-10 w-10 text-red-400" />
          <p className="text-white/70 text-sm text-center px-6">{errorMsg}</p>
          <button
            onClick={() => setStatus("idle")}
            className="text-xs font-bold text-[#FF6B1A] hover:underline"
          >
            Réessayer
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
